import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';

interface GroupedQueries {
    [head: string]: { id: number; name: string }[];
}

const EL_GeneralQuery: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    // --- State ---
    const [groupedQueries, setGroupedQueries] = useState<GroupedQueries>({});
    const [selectedQueryId, setSelectedQueryId] = useState<string>('0');

    const [databases] = useState(['eLearnWeb', 'YLA', 'Dis.Board', 'CPP']);
    const [selectedDb, setSelectedDb] = useState('eLearnWeb');

    const [tables, setTables] = useState<string[]>([]);
    const [selectedTable, setSelectedTable] = useState('-- Select --');

    const [sqlText, setSqlText] = useState('');
    const [authCode, setAuthCode] = useState('');
    const [timeout, setTimeoutVal] = useState('60');

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [opMessage, setOpMessage] = useState('');
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const [alertInfo, setAlertInfo] = useState({
        show: false,
        message: ''
    });

    // Dynamic Grid State
    const [gridColumns, setGridColumns] = useState<string[]>([]);
    const [gridRows, setGridRows] = useState<any[]>([]);

    // Pagination / grid toolbar state
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [showNulls, setShowNulls] = useState(false);
    const [lastRefreshTs, setLastRefreshTs] = useState<string>('');

    // Simulated session variables
    // const isSuperAdmin = sessionStorage.getItem("SES_SUPERADMIN") === "AAAA";
    // 16/09/2026 added superadmin condition
    const login =
        sessionStorage.getItem("L") ||
        sessionStorage.getItem("UserName") ||
        '';

    useEffect(() => {
        const admins = [
            'Shubhanga',
            'shubhanga',
            'SHUBHANGA',
            'Shashanka',
            'shashanka',
            'SHASHANKA',
            'VIKAS',
            'vikas',
            'Vikas'
        ];

        setIsSuperAdmin(admins.includes(login));
    }, [login]);

    document.body.style.backgroundColor = "#fff";

    // --- Effects ---
    useEffect(() => {
        document.body.style.margin = '5px';
        fetchQueries();
    }, []);

    useEffect(() => {
        if (isSuperAdmin) fetchTables();
    }, [selectedDb]);

    // --- Data Fetching ---
    const fetchQueries = async () => {
        setLoading(true);
        setErrorMsg('');

        try {
            const res = await fetch(`${API_BASE_URL}api/general_queries/list`);
            const json = await res.json();
            if (json.grouped_queries) setGroupedQueries(json.grouped_queries);
        } catch (err) {
            setErrorMsg("Failed to load saved queries.");
        } finally {
            setLoading(false);
        }
    };

    const fetchTables = async () => {
        setLoading(true);
        setErrorMsg('');

        try {
            const res = await fetch(`${API_BASE_URL}api/general_queries/tables?db=${selectedDb}`);
            const json = await res.json();
            if (json.tables) {
                setTables(json.tables);
                setSelectedTable('-- Select --');
            }
        } catch (err) {
            setErrorMsg("Failed to load tables.");
        } finally {
            setLoading(false);
        }
    };

    const handleQuerySelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const qid = e.target.value;
        setSelectedQueryId(qid);
        if (qid === '0') return;

        setLoading(true);
        setErrorMsg('');

        try {
            const res = await fetch(`${API_BASE_URL}api/general_queries/get?id=${qid}`);
            const json = await res.json();
            if (json.sql) setSqlText(json.sql);
        } catch (err) {
            setErrorMsg("Failed to load query text.");
        } finally {
            setLoading(false);
        }
    };

    // --- UI Actions ---
    const handleFields = () => {
        if (selectedTable !== '-- Select --') {
            setSqlText(`sp_columns ${selectedTable}`);
        }
    };

    const handleData = () => {
        if (selectedTable !== '-- Select --') {
            setSqlText(`SELECT * FROM ${selectedTable}`);
        }
    };
    const showErrorAlert = (message: string) => {
        setAlertInfo({
            show: true,
            message
        });
    };

    const handleGo = async () => {
        if (!sqlText.trim()) {
            showErrorAlert("Please enter a SQL query.");
            return;
        }

        setLoading(true);
        setErrorMsg('');
        setOpMessage('');
        setGridColumns([]);
        setGridRows([]);
        setCurrentPage(1);

        try {
            const res = await fetch(
                `${API_BASE_URL}api/general_queries/execute`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        sql: sqlText,
                        db: selectedDb,
                        auth_code: authCode,
                        is_super_admin: isSuperAdmin
                    })
                }
            );

            const json = await res.json();

            // --------------------------------------------------
            // BACKEND ERROR
            // --------------------------------------------------
            if (!res.ok || json.error) {
                showErrorAlert(
                    json.error || "Error executing query."
                );
                return;
            }

            // --------------------------------------------------
            // SELECT RESULT
            // --------------------------------------------------
            if (json.type === 'select') {
                setGridColumns(json.columns || []);
                setGridRows(json.rows || []);
                setCurrentPage(1);

                setLastRefreshTs(
                    new Date()
                        .toISOString()
                        .replace('T', ' ')
                        .substring(0, 19)
                    + ' UTC.'
                );
            }

            // --------------------------------------------------
            // INSERT / UPDATE / DELETE
            // --------------------------------------------------
            else {
                setOpMessage(
                    `${json.affected_rows || 0} Row(s) affected`
                );
            }

        } catch (err) {
            console.error("Query execution error:", err);

            showErrorAlert(
                "Error executing query."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = (e: React.MouseEvent) => {
        e.preventDefault();
        handleGo();
    };

    const handleExit = () => {
        window.location.href = "/SysAdmin";
    };

    const handleSaveQuery = () => {
        sessionStorage.setItem("TMP_SQRY", sqlText);
        window.location.href = `/WEBADMIN/REPORTS/EL_SaveStructuredReport?ID=${selectedQueryId}`;
    };

    const handleSaveQueryAs = () => {
        sessionStorage.setItem("TMP_SQRY", sqlText);
        window.location.href = "/WEBADMIN/REPORTS/EL_SaveStructuredReport";
    };

    // --- Pagination helpers ---
    const totalPages = Math.max(1, Math.ceil(gridRows.length / rowsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const pagedRows = gridRows.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);

    const goFirst = () => setCurrentPage(1);
    const goPrev = () => setCurrentPage(p => Math.max(1, p - 1));
    const goNext = () => setCurrentPage(p => Math.min(totalPages, p + 1));
    const goLast = () => setCurrentPage(totalPages);

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value, 10);
        setRowsPerPage(val > 0 ? val : 1);
        setCurrentPage(1);
    };

    const renderCellValue = (val: any) => {
        if (val === null || val === undefined) {
            return showNulls ? 'NULL' : '';
        }
        return String(val);
    };

    // --- Styles mapping to eLearnCSS.css ---
    const styles = {
        container: { fontFamily: 'Trebuchet MS', fontSize: '10pt' },
        controlPanel: { width: '100%', border: 'solid 1px #8080FF', backgroundColor: '#EBE9FD', padding: '5px' },
        table: { width: '100%', borderSpacing: 0, cellPadding: '5px' },
        tdLabel: { width: '20%', verticalAlign: 'top' as const },
        tdContent: { width: '80%', verticalAlign: 'top' as const },

        // Form Controls
        select: { fontFamily: 'Courier New', fontSize: '14pt', width: '50%' },
        inputSmall: { border: 'solid 1px black', width: '40px', marginLeft: '5px' },
        authInput: { border: 'solid 1px #00cc00', width: '100px', marginLeft: '10px' },
        textarea: { width: '99%', fontFamily: 'Arial', fontSize: '12pt', height: '140px', border: 'solid 1px #003300' },

        // Match cssButtonWithBorder
        btnBorder: {
            backgroundColor: '#119ECC', fontFamily: 'Trebuchet MS', fontSize: '10pt',
            fontWeight: 'bold', color: 'White', border: 'solid 2px orange',
            height: '28px', width: '80px', cursor: 'pointer', marginLeft: '10px', marginBottom: '5px'
        },

        // Pagination toolbar (above grid, matches screenshot)
        paginationBar: {
            display: 'flex' as const,
            alignItems: 'center' as const,
            justifyContent: 'space-between' as const,
            width: '100%',
            border: 'solid 1px #80a0ff',
            borderBottom: 'none',
            backgroundColor: '#CFE2FF',
            color: '#2859ac',
            fontFamily: 'Arial',
            fontSize: '10pt',
            padding: '5px 40px',
            marginTop: '10px',
            boxSizing: 'border-box' as const
        },
        paginationRight: { display: 'flex' as const, alignItems: 'center' as const, gap: '14px' },
        paginationInput: { width: '40px', marginLeft: '5px', border: 'solid 1px #80a0ff', textAlign: 'center' as const },
        paginationLink: { color: '#2859ac', textDecoration: 'underline', cursor: 'pointer', fontSize: "12pt" },
        paginationLinkDisabled: { color: '#a0b4d0', textDecoration: 'none', cursor: 'default', pointerEvents: 'none' as const, fontSize: "12pt" },

        // Dynamic Grid matching eLearnCSS
        gridWrapper: { width: '100%', border: 'solid 1px #80a0ff', overflow: 'auto' as const, maxHeight: '600px' },
        gridTable: { width: '100%', borderCollapse: 'collapse' as const, fontFamily: 'Arial', fontSize: '8pt', color: 'black' },
        gridHeader: {
            backgroundColor: '#CFE2FF', fontSize: "14px", color: '#2859ac', border: "1px solid #80a0ff",
            padding: '10px', textAlign: 'left' as const, fontWeight: 'normal',
            position: 'sticky' as const, top: 0, zIndex: 2
        },
        gridHeaderSerial: {
            backgroundColor: '#CFE2FF', fontSize: "14px", color: '#2859ac', border: "1px solid #80a0ff",
            padding: '10px', textAlign: 'center' as const, fontWeight: 'normal', width: '50px',
            position: 'sticky' as const, top: 0, zIndex: 2
        },
        gridCell: { padding: '8px', borderRight: '1px solid #80a0ff', fontSize: '14px' },
        gridCellSerial: { padding: '8px', borderRight: '1px solid #80a0ff', fontSize: '14px', textAlign: 'center' as const, color: '#666' },
        error: { color: 'red', fontWeight: 'bold', marginTop: '10px' }
    };

    return (
        <div style={styles.container}>
            {loading && <Loading />}

            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() =>
                        setAlertInfo({
                            show: false,
                            message: ''
                        })
                    }
                />
            )}

            <div style={styles.controlPanel}>
                <table style={styles.table}>
                    <tbody>
                        {/* Top Row: Query Dropdown & Execute */}
                        <tr>
                            <td style={styles.tdLabel}>&nbsp;Available Reports:</td>
                            <td style={styles.tdContent} colSpan={2}>
                                <select style={styles.select} value={selectedQueryId} onChange={handleQuerySelect}>
                                    <option value="0">--Select--</option>
                                    {Object.keys(groupedQueries).map(head => (
                                        <optgroup key={head} label={head} style={{ color: 'white', backgroundColor: '#808080', fontStyle: 'normal' }}>
                                            {groupedQueries[head].map(q => (
                                                <option key={q.id} value={q.id} style={{ color: 'black', backgroundColor: 'white' }}>
                                                    &nbsp;&nbsp;{q.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>

                                &nbsp;&nbsp;&nbsp;&nbsp;Timeout: &nbsp;
                                <input style={styles.inputSmall} value={timeout} onChange={(e) => setTimeoutVal(e.target.value)} />

                                {isSuperAdmin && (
                                    <input type="password" style={styles.authInput} placeholder="Auth Code" value={authCode} onChange={(e) => setAuthCode(e.target.value)} />
                                )}

                                <button style={styles.btnBorder} onClick={handleGo}>Start</button>
                                <button style={styles.btnBorder} onClick={handleExit}>Exit</button>
                            </td>
                        </tr>

                        {/* Middle Row: DB/Table Selection & Textarea */}
                        {isSuperAdmin && (
                            <tr>
                                <td style={styles.tdLabel}>
                                    &nbsp;DB:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    <select style={{ width: '150px' }} value={selectedDb} onChange={(e) => setSelectedDb(e.target.value)}>
                                        {databases.map(db => <option key={db} value={db}>{db}</option>)}
                                    </select>
                                    <br /><br />
                                    &nbsp;TABLES:&nbsp;
                                    <select style={{ width: '150px' }} value={selectedTable} onChange={(e) => setSelectedTable(e.target.value)}>
                                        <option value="-- Select --">-- Select --</option>
                                        {tables.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    <br /><br />
                                    &nbsp;<button style={styles.btnBorder} onClick={handleFields}>Fields</button>
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                    <button style={styles.btnBorder} onClick={handleData}>Data</button>
                                    <br /><hr />
                                    &nbsp;<button style={{ ...styles.btnBorder, width: '100px' }} onClick={handleSaveQuery} disabled={selectedQueryId === '0'}>Save Query</button>
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                    <button style={styles.btnBorder} onClick={handleSaveQueryAs}>Save As </button>
                                </td>
                                <td colSpan={2}>
                                    <textarea
                                        style={styles.textarea}
                                        value={sqlText}
                                        onChange={(e) => setSqlText(e.target.value)}
                                        placeholder="Enter SQL Query here..."
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* {errorMsg && <div style={styles.error}>{errorMsg}</div>} */}
            {opMessage && <div style={{ marginTop: '10px', fontWeight: 'bold' }}>{opMessage}</div>}

            {/* Dynamic Results Grid */}
            {gridColumns.length > 0 && (
                <>
                    {/* Pagination toolbar — sits above the table, stays put while table body scrolls */}
                    <div style={styles.paginationBar}>
                        <span>{lastRefreshTs}</span>
                        <div style={styles.paginationRight}>
                            <label>
                                <input type="checkbox" checked={showNulls} onChange={(e) => setShowNulls(e.target.checked)} />
                                &nbsp;Show Nulls.
                            </label>
                            <span>
                                Rows per page:
                                <input
                                    style={styles.paginationInput}
                                    value={rowsPerPage}
                                    onChange={handleRowsPerPageChange}
                                />
                            </span>
                            <a href="#" style={{ ...styles.paginationLink, fontSize: "10pt" }} onClick={handleRefresh} >Refresh</a>
                            <span>
                                <a
                                    href="#"
                                    style={safePage === 1 ? styles.paginationLinkDisabled : styles.paginationLink}
                                    onClick={(e) => { e.preventDefault(); goFirst(); }}
                                >&laquo;</a>
                                &nbsp;
                                <a
                                    href="#"
                                    style={safePage === 1 ? styles.paginationLinkDisabled : styles.paginationLink}
                                    onClick={(e) => { e.preventDefault(); goPrev(); }}
                                >&lt;</a>
                                &nbsp;{safePage} of {totalPages}&nbsp;
                                <a
                                    href="#"
                                    style={safePage === totalPages ? styles.paginationLinkDisabled : styles.paginationLink}
                                    onClick={(e) => { e.preventDefault(); goNext(); }}
                                >&gt;</a>
                                &nbsp;
                                <a
                                    href="#"
                                    style={safePage === totalPages ? styles.paginationLinkDisabled : styles.paginationLink}
                                    onClick={(e) => { e.preventDefault(); goLast(); }}
                                >&raquo;</a>
                            </span>
                        </div>
                    </div>

                    <div style={styles.gridWrapper}>
                        <table style={styles.gridTable}>
                            <thead>
                                <tr>
                                    <th style={styles.gridHeaderSerial}>#</th>
                                    {gridColumns.map((col, i) => (
                                        <th key={i} style={styles.gridHeader}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pagedRows.map((row, rIdx) => (
                                    <tr key={rIdx} style={{ backgroundColor: rIdx % 2 === 0 ? 'white' : '#f4faff' }}>
                                        <td style={styles.gridCellSerial}>{(safePage - 1) * rowsPerPage + rIdx + 1}</td>
                                        {gridColumns.map((col, cIdx) => (
                                            <td key={cIdx} style={styles.gridCell}>{renderCellValue(row[col])}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default EL_GeneralQuery;